"use client";

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';

const values = [
{
  icon:
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>,

  title: 'Accessibilité',
  text: 'Des soins accessibles à tous, sans exception. Nos centres accueillent chaque patient avec la même attention, quelles que soient sa situation et son origine.',
  color: '#DB052C'
},
{
  icon:
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>,

  title: 'Humanité',
  text: 'Une équipe à l\'écoute, attentionnée et bienveillante. Nous plaçons la relation humaine au cœur de chaque consultation, pour vous accompagner avec douceur.',
  color: '#DB052C'
},
{
  icon:
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>,

  title: 'Qualité',
  text: 'Nos centres rassemblent des professionnels expérimentés issus de nombreuses spécialités. Leur expertise constitue le fondement d\'une prise en charge rigoureuse, adaptée aux besoins de chaque patient.',
  color: '#DB052C'
}];


export default function ValuesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.value-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add('reveal-visible');
                card.classList.remove('reveal-hidden');
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="valeurs" className="py-20 md:py-28 bg-bg-gray" aria-labelledby="values-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section title */}
        <div className="text-center mb-16">
          <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Nos engagements
          </p>
          <h2
            id="values-title"
            className="text-3xl md:text-4xl font-semibold text-text-main mb-4"
            style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.01em' }}>
            
            Ce qui nous anime chaque jour
          </h2>
          <span className="section-decorator" aria-hidden="true" />
          <p className="text-text-muted text-base leading-relaxed max-w-2xl mx-auto mt-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Au cœur de notre réseau, trois valeurs fondamentales guident chaque geste, chaque décision et chaque interaction avec nos patients.
          </p>
        </div>

        {/* Section image + Values grid side by side */}
        <div className="flex flex-col lg:flex-row gap-10 items-stretch" ref={sectionRef}>
          {/* Image on the left */}
          <div className="lg:w-1/2 rounded-2xl overflow-hidden shadow-card flex-shrink-0">
            <Image
              src="/assets/images/valeurs-polyclinique.png"
              alt="Accueil d'une polyclinique César De Paepe"
              width={1200}
              height={896}
              className="w-full h-full object-cover"
              style={{ minHeight: '320px', objectPosition: 'center' }} />
          </div>

          {/* Values grid on the right */}
          <div className="lg:w-1/2 flex flex-col gap-6">
            {values?.map((value, index) =>
            <div
              key={value?.title}
              className={`value-card reveal-hidden bg-white rounded-card p-6 shadow-card card-hover flex items-start gap-5 delay-${(index + 1) * 100}`}
              role="article"
              aria-label={`Valeur : ${value?.title}`}>
              
                {/* Icon circle */}
                <div
                className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: value?.color }}
                aria-hidden="true">
                  {value?.icon}
                </div>

                <div>
                  <h3
                  className="text-lg font-semibold text-text-main mb-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {value?.title}
                  </h3>

                  <p
                  className="text-text-muted text-sm leading-relaxed"
                  style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {value?.text}
                  </p>

                  {/* Accent line */}
                  <div className="w-8 h-0.5 bg-primary mt-4 rounded-full" aria-hidden="true" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}